import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_URL } from "../lib/api";
import {
  RENA, useRenalifePage, useRenalifeCta, Eyebrow, PageShell, LoadingScreen, NotFoundScreen, VideoBlock, Footer,
} from "./RenalifeLanding";

const DEADLINE = new Date("2026-09-30T23:59:59-06:00");

const useCountdown = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, DEADLINE.getTime() - now);
  return { days: Math.floor(ms / 86400000), hours: Math.floor((ms % 86400000) / 3600000), closed: ms === 0 };
};

const INITIALS = ["MG", "JR", "AL", "KP", "RD", "SV", "LM", "CT", "PA", "EB", "NF", "OH", "DZ", "YC", "IQ", "TW"];

export default function RenalifeFundador() {
  const { slug } = useParams();
  const { data, notFound } = useRenalifePage(slug);
  const { go, sending } = useRenalifeCta(slug, "bono4");
  const { days, hours, closed } = useCountdown();

  if (notFound) return <NotFoundScreen />;
  if (!data) return <LoadingScreen />;

  const img = data.assets?.bono4_fundador?.image_url;
  const video = data.assets?.bono4_fundador?.video_url;

  return (
    <PageShell testid="renalife-landing-bono4">
      {/* Countdown superior discreto */}
      <div data-testid="founder-countdown" className="text-center py-2.5 font-mono text-xs tracking-widest" style={{ background: "#01201790", color: RENA.gold, borderBottom: `1px solid ${RENA.gold}25` }}>
        {closed ? "La ventana Fundador ha cerrado" : `Ventana Fundador cierra en ${days} días : ${hours} horas`}
      </div>

      {/* 1) HERO cinematográfico */}
      <section className="min-h-screen flex items-center px-6 sm:px-12" style={{ background: `linear-gradient(160deg, ${RENA.deep} 0%, #011811 130%)` }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[3fr_2fr] gap-12 items-center py-16 w-full">
          <div>
            <Eyebrow>BONO 004 · UNA VEZ EN LA VIDA DEL PROYECTO</Eyebrow>
            <h1 className="font-renalife font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.12] mt-6">
              Solo los que llegaron primero<br />se llamaron<br /><em className="italic font-medium" style={{ color: RENA.gold }}>Fundadores</em>.
            </h1>
            <p className="text-base sm:text-lg mt-7 max-w-xl" style={{ color: "#cbd5d1" }}>
              Regístrate en Renalife México durante agosto o septiembre de 2026 y recibe — de por vida — un 10%
              adicional sobre las ganancias del Bono Infinito de todos tus directos. Después de septiembre,
              esta puerta se cierra permanentemente.
            </p>
            <button
              data-testid="cta-founder"
              onClick={go}
              disabled={sending}
              className="mt-10 rounded-full px-10 py-5 font-bold text-lg transition-transform hover:-translate-y-0.5"
              style={{ background: RENA.gold, color: RENA.deep }}
            >
              {sending ? "Abriendo WhatsApp…" : "Sellar mi lugar como Fundador"}
            </button>
          </div>
          {img && (
            <img data-testid="hero-image-bono4" src={`${BACKEND_URL}${img}`} alt="Fundador Renalife" className="rounded-3xl border shadow-2xl w-full object-cover max-h-[600px]" style={{ borderColor: `${RENA.gold}30` }} />
          )}
        </div>
      </section>

      {/* 2) ¿QUÉ ES EXACTAMENTE UN FUNDADOR? */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>¿Qué es exactamente un Fundador?</Eyebrow>
        <div data-testid="founder-definition" className="mt-6 space-y-5 text-base sm:text-lg leading-relaxed" style={{ color: "#cbd5d1" }}>
          <p>
            El sello Fundador es una marca ligada a tu perfil, no un rango ganable. No depende de cuánto vendas,
            de cuántas personas registres ni de qué tan rápido crezcas. Depende de una sola cosa: haber estado
            aquí cuando el proyecto abrió sus puertas en México.
          </p>
          <p>
            Se activa automáticamente si te registras entre el 1 de agosto y el 30 de septiembre de 2026. Desde ese
            momento, cada peso de Bono Infinito que generen tus directos N1 te produce un 10% adicional — cada mes,
            mientras te mantengas activo.
          </p>
          <p>
            No se puede comprar después. No se hereda. No se transfiere. Cuando la ventana cierre, el registro de
            Fundadores queda sellado para siempre.
          </p>
        </div>
      </section>

      {/* 3) LA MATEMÁTICA DEL 10% */}
      <section className="px-6 sm:px-12 py-20" style={{ background: "#03251c" }}>
        <div className="max-w-4xl mx-auto">
          <Eyebrow>La matemática del 10%</Eyebrow>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div data-testid="math-example-1" className="rounded-2xl border p-7" style={{ borderColor: `${RENA.gold}30`, background: RENA.deep }}>
              <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: "#94a3b8" }}>Ejemplo con 1 directa</div>
              <p className="text-sm sm:text-base mt-3 leading-relaxed" style={{ color: "#cbd5d1" }}>
                Si Ana (tu directa) genera <b style={{ color: RENA.off }}>$5,000 MXN</b> de Bono Infinito el mes de octubre,
                tú — como Fundador — recibes
              </p>
              <div className="font-renalife text-3xl mt-3" style={{ color: RENA.gold }}>$500 MXN adicionales</div>
              <p className="text-xs mt-2" style={{ color: "#94a3b8" }}>solo por haber sido primero.</p>
            </div>
            <div data-testid="math-example-2" className="rounded-2xl border p-7" style={{ borderColor: `${RENA.gold}60`, background: "#04382b" }}>
              <div className="font-mono text-[11px] uppercase tracking-widest" style={{ color: RENA.gold }}>Ejemplo con 5 directos</div>
              <p className="text-sm sm:text-base mt-3 leading-relaxed" style={{ color: "#cbd5d1" }}>
                Tus 5 directos generan $2,000 MXN cada uno: <b style={{ color: RENA.off }}>$10,000 combinados</b>.
                Tu override Fundador:
              </p>
              <div className="font-renalife text-3xl mt-3" style={{ color: RENA.gold }}>$1,000 MXN. Cada. Mes.</div>
              <p className="text-xs mt-2" style={{ color: "#94a3b8" }}>Ejemplos de cálculo, no una promesa de ingresos.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4) HALL OF FOUNDERS */}
      <section className="max-w-4xl mx-auto px-6 sm:px-12 py-20 text-center">
        <Eyebrow>Hall of Founders</Eyebrow>
        <h2 className="font-renalife text-2xl sm:text-3xl mt-4">Los primeros ya sellaron su lugar.</h2>
        <div data-testid="founders-grid" className="grid grid-cols-8 gap-3 max-w-lg mx-auto mt-10">
          {INITIALS.map((ini) => (
            <div key={ini} className="aspect-square rounded-full flex items-center justify-center text-xs font-bold blur-[3px] select-none" style={{ background: "#064E3B", color: `${RENA.gold}90`, border: `1px solid ${RENA.gold}30` }}>
              {ini}
            </div>
          ))}
        </div>
        <div data-testid="founders-count" className="font-mono text-sm mt-8" style={{ color: "#cbd5d1" }}>
          Fundadores actuales: <span style={{ color: RENA.gold }}>128</span> / cupo ilimitado
        </div>
        <p className="text-xs mt-2" style={{ color: "#64748b" }}>Sin límite de cupo. Con límite de fecha.</p>
      </section>

      <VideoBlock videoUrl={video} title="El Bono Fundador en 30 segundos" />

      {/* 5) EL RECORDATORIO FINAL */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 py-28 text-center">
        <p data-testid="final-reminder" className="font-renalife text-2xl sm:text-4xl leading-relaxed">
          El 30 de septiembre de 2026,<br />
          este bono deja de existir <em className="italic" style={{ color: RENA.gold }}>para siempre</em>.<br />
          <span className="text-lg sm:text-2xl block mt-6" style={{ color: "#cbd5d1" }}>
            Los que estén dentro, cobran el 10% cada mes por el resto de su relación con Renalife.
          </span>
        </p>
      </section>

      {/* 6) CTA FINAL FULL-BLEED */}
      <button
        data-testid="cta-founder-final"
        onClick={go}
        disabled={sending}
        className="w-full py-9 font-renalife font-bold text-2xl sm:text-3xl transition-opacity hover:opacity-90"
        style={{ background: RENA.gold, color: RENA.deep }}
      >
        Registrarme como Fundador →
      </button>
      <div className="text-center py-4 text-[11px]" style={{ background: RENA.deep, color: "#64748b" }}>
        Al registrarte aceptas los <a href="/terminos" className="underline hover:opacity-80">Términos</a> y el{" "}
        <a href="/aviso-privacidad" className="underline hover:opacity-80">Aviso de Privacidad</a>. Responsable: Ricardo Zermeño Mejía.
      </div>
      <Footer name={data.distributor_name} />
    </PageShell>
  );
}
